import { createSignal, Show, onMount, onCleanup, createEffect, createMemo } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import socket from '../socket';
import QuizQuestionPreview from '../QuizQuestionPreview';
import { useUserContext } from '../UserContext';
import ExternalNameForm from '../ExternalNameForm';
import { Loader } from '../Components';
import type { QuizJoinMethods } from '../../shared/quizSettings';

interface LiveState {
    quizCode: string;
    quizTitle: string;
    questionIndex: number | null;
    status: 'hidden' | 'shown' | 'active' | 'stopped';
    question: any | null;
    serverNow?: number;
    receivedAt?: number;
    phaseStartedAt?: number | null;
    phaseEndsAt?: number | null;
    hideQuestionsFromStudents?: boolean;
}

interface QuizJoinOptionsPayload {
    quizCode: string;
    quizTitle: string;
    joinMethods: QuizJoinMethods;
}

interface QuizAuthMethodNotAllowedPayload {
    quizCode: string;
    joinMethods: QuizJoinMethods;
    message?: string;
}

export default function StudentQuiz() {
    const params = useParams();
    const navigate = useNavigate();
    const [userStore, _setUserStore, userActions] = useUserContext() || (() => { throw new Error('UserContext not found'); })();

    const quizCode = () => (params.code ?? '').toUpperCase();

    const [state, setState] = createSignal<LiveState | null>(null);
    const [quizTitle, setQuizTitle] = createSignal<string>('');
    const [notFound, setNotFound] = createSignal(false);
    const [joinMethods, setJoinMethods] = createSignal<QuizJoinMethods | null>(null);
    const [joinMethodError, setJoinMethodError] = createSignal<string | null>(null);
    const [isLoadingJoinMethods, setIsLoadingJoinMethods] = createSignal(true);
    const [didTryAutoAnonymous, setDidTryAutoAnonymous] = createSignal(false);

    // Store answers per question index.
    const [answersMap, setAnswersMap] = createSignal<Record<number, string[]>>({});
    const [isSaving, setIsSaving] = createSignal(false);
    const [now, setNow] = createSignal(Date.now());

    const timerProgress = createMemo(() => {
        const currentState = state();
        const startedAt = currentState?.phaseStartedAt;
        const endsAt = currentState?.phaseEndsAt;
        if (!startedAt || !endsAt || endsAt <= startedAt) return null;
        const estimatedServerNow = currentState.serverNow && currentState.receivedAt
            ? currentState.serverNow + (now() - currentState.receivedAt)
            : now();
        const visualEndsAt = Math.max(startedAt, endsAt - 1000);
        if (visualEndsAt === startedAt) return 100;
        return Math.min(100, Math.max(0, ((estimatedServerNow - startedAt) / (visualEndsAt - startedAt)) * 100));
    });

    const canJoinWithCAS = createMemo(() => !!joinMethods()?.cas);
    const canJoinWithNamed = createMemo(() => !!joinMethods()?.named);
    const canJoinWithAnonymous = createMemo(() => !!joinMethods()?.anonymous);
    const onlyAnonymousJoin = createMemo(() => canJoinWithAnonymous() && !canJoinWithCAS() && !canJoinWithNamed());

    // Get current question's saved answers.
    const currentAnswers = createMemo(() => {
        const currentState = state();
        if (!currentState || currentState.questionIndex === null) return [];
        return answersMap()[currentState.questionIndex] || [];
    });

    const handleAnswerSelect = (answers: string[]) => {
        if (!userStore.isAuthenticated) return;

        const currentState = state();
        if (!currentState || currentState.questionIndex === null) return;

        // Update local answers map.
        setAnswersMap(prev => ({
            ...prev,
            [currentState.questionIndex!]: answers
        }));

        // Auto-submit to server.
        setIsSaving(true);
        socket.emit('student-submit-answer', {
            quizCode: quizCode(),
            questionIndex: currentState.questionIndex,
            selectedAnswers: answers
        });

        // Visual feedback that we are saving.
        setTimeout(() => setIsSaving(false), 500);
    };

    createEffect(() => {
        const code = quizCode();
        setNotFound(false);
        setState(null);
        setJoinMethodError(null);
        setJoinMethods(null);
        setIsLoadingJoinMethods(true);
        setDidTryAutoAnonymous(false);
        socket.emit('quiz-get-join-options', { quizCode: code });
    });

    createEffect(() => {
        if (userStore.isAuthenticated) {
            socket.emit('student-join-quiz', { quizCode: quizCode() });
            socket.emit('student-get-answers', { quizCode: quizCode() });
        }
    });

    createEffect(() => {
        if (!userStore.isAuthenticated && !userStore.isAuthenticating && onlyAnonymousJoin() && !didTryAutoAnonymous()) {
            setDidTryAutoAnonymous(true);
            userActions.submitAnonymousLogin();
        }
    });

    onMount(() => {
        const clock = window.setInterval(() => setNow(Date.now()), 50);
        const handleQuizNotFound = (data: { quizCode: string }) => {
            if (data.quizCode === quizCode()) {
                setNotFound(true);
                setIsLoadingJoinMethods(false);
            }
        };

        const handleQuizJoinOptions = (data: QuizJoinOptionsPayload) => {
            if (data.quizCode === quizCode()) {
                setQuizTitle(data.quizTitle);
                setJoinMethods(data.joinMethods);
                setIsLoadingJoinMethods(false);
            }
        };

        const handleAuthMethodNotAllowed = (data: QuizAuthMethodNotAllowedPayload) => {
            if (data.quizCode === quizCode()) {
                setJoinMethods(data.joinMethods);
                setJoinMethodError(data.message || 'Your login method is not allowed for this quiz.');
                setState(null);
            }
        };

        const handleLiveQuizState = (s: LiveState) => {
            if (s.quizCode === quizCode()) {
                setState({ ...s, receivedAt: Date.now() });
                setQuizTitle(s.quizTitle);
            }
        };

        const handleLiveQuizUpdate = (s: LiveState) => {
            if (s.quizCode === quizCode()) {
                setState({ ...s, receivedAt: Date.now() });
            }
        };

        const handleStudentAnswersData = (data: { quizCode: string; answers: Record<number, string[]> }) => {
            if (data.quizCode === quizCode()) {
                setAnswersMap(data.answers);
            }
        };

        socket.on('quiz-not-found', handleQuizNotFound);
        socket.on('quiz-join-options', handleQuizJoinOptions);
        socket.on('quiz-auth-method-not-allowed', handleAuthMethodNotAllowed);
        socket.on('live-quiz-state', handleLiveQuizState);
        socket.on('live-quiz-update', handleLiveQuizUpdate);
        socket.on('student-answers-data', handleStudentAnswersData);

        onCleanup(() => {
            window.clearInterval(clock);
            socket.off('quiz-not-found', handleQuizNotFound);
            socket.off('quiz-join-options', handleQuizJoinOptions);
            socket.off('quiz-auth-method-not-allowed', handleAuthMethodNotAllowed);
            socket.off('live-quiz-state', handleLiveQuizState);
            socket.off('live-quiz-update', handleLiveQuizUpdate);
            socket.off('student-answers-data', handleStudentAnswersData);
        });
    });

    return (
        <>
            {/* Minimal header for student quiz */}
            <div class="student-quiz-header" style={{
                "background-color": "#f8f9fa",
                "border-bottom": "1px solid #dee2e6",
                "padding": "0.75rem 1rem",
                "display": "flex",
                "align-items": "center",
                "gap": "1rem",
                "position": "sticky",
                "top": "0",
                "z-index": "1000",
                ...(state()?.status === 'stopped' && { "color": "#969696ff" })
            }}>
                <button
                    class="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate('/')}
                    title="Go back to home"
                >
                    ← Back
                </button>
                <h5 class="mb-0 flex-grow-1">{state()?.quizTitle || quizTitle() || 'Loading quiz...'}</h5>
                <Show when={isSaving()}>
                    <span class="badge bg-info">Saving...</span>
                </Show>
                <Show when={timerProgress() !== null}>
                    <div
                        role="progressbar"
                        aria-label="Time elapsed"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={Math.round(timerProgress()!)}
                        style={{
                            position: 'absolute',
                            left: '0',
                            bottom: '0',
                            width: '100%',
                            height: '4px',
                            'background-color': state()?.status === 'active' ? '#198754' : '#0d6efd',
                            transform: `scaleX(${timerProgress()! / 100})`,
                            'transform-origin': 'left center',
                            transition: 'transform 50ms linear',
                            'will-change': 'transform'
                        }}
                    />
                </Show>
            </div>

            <div class="container py-3">
                <Show when={!notFound()} fallback={<div class="alert alert-danger">Quiz code not found.</div>}>
                    <Show
                        when={userStore.isAuthenticated}
                        fallback={
                            <div class="card p-3" style={{ 'max-width': '560px' }}>
                                <h5 class="mb-2">Join Quiz {quizCode()}</h5>
                                <Show when={quizTitle()}>
                                    <div class="text-muted mb-3">{quizTitle()}</div>
                                </Show>

                                <Show when={!isLoadingJoinMethods()} fallback={<Loader />}>
                                    <Show when={joinMethodError()}>
                                        <div class="alert alert-warning">{joinMethodError()}</div>
                                    </Show>

                                    <Show when={userStore.isAuthenticating} fallback={
                                        <Show when={userStore.authStage === 'enter-external-name' && canJoinWithNamed()} fallback={
                                            <>
                                                <p class="mb-2">Choose how you want to join:</p>
                                                <div class="d-flex flex-wrap gap-2">
                                                    <Show when={canJoinWithCAS()}>
                                                        <button class="btn btn-primary" type="button" onClick={() => userActions.chooseCAS()}>
                                                            Sign in with CAS
                                                        </button>
                                                    </Show>
                                                    <Show when={canJoinWithNamed()}>
                                                        <button class="btn btn-secondary" type="button" onClick={() => userActions.chooseExternal()}>
                                                            Continue with your name
                                                        </button>
                                                    </Show>
                                                    <Show when={canJoinWithAnonymous()}>
                                                        <button class="btn btn-outline-secondary" type="button" onClick={() => userActions.submitAnonymousLogin()}>
                                                            Continue anonymously
                                                        </button>
                                                    </Show>
                                                </div>
                                                <Show when={onlyAnonymousJoin()}>
                                                    <div class="form-text mt-2">Connecting anonymously...</div>
                                                </Show>
                                            </>
                                        }>
                                            <ExternalNameForm allowCASFallback={canJoinWithCAS()} />
                                        </Show>
                                    }>
                                        <Loader />
                                    </Show>
                                </Show>
                            </div>
                        }
                    >
                        <Show when={!joinMethodError()} fallback={
                            <div class="card p-3" style={{ 'max-width': '560px' }}>
                                <h5 class="mb-2">Cannot Join This Quiz</h5>
                                <div class="alert alert-warning mb-2">{joinMethodError()}</div>
                                <p class="mb-2">Choose an allowed login method:</p>
                                <div class="d-flex flex-wrap gap-2">
                                    <Show when={canJoinWithCAS()}>
                                        <button class="btn btn-primary" type="button" onClick={() => userActions.chooseCAS()}>
                                            Use CAS
                                        </button>
                                    </Show>
                                    <Show when={canJoinWithNamed()}>
                                        <button class="btn btn-secondary" type="button" onClick={() => userActions.chooseExternal()}>
                                            Use Student Name
                                        </button>
                                    </Show>
                                    <Show when={canJoinWithAnonymous()}>
                                        <button class="btn btn-outline-secondary" type="button" onClick={() => userActions.submitAnonymousLogin()}>
                                            Use Anonymous Login
                                        </button>
                                    </Show>
                                </div>
                            </div>
                        }>
                            <Show when={state()}>
                                <Show when={state()!.hideQuestionsFromStudents}>
                                    <div class="alert alert-info">
                                        <p class="mb-0">Question visible on teacher's display</p>
                                    </div>
                                </Show>
                                <Show when={state()!.status !== 'hidden'} fallback={<div class="alert alert-info">The next question will appear here.</div>}>
                                    <Show when={!state()!.hideQuestionsFromStudents}>
                                        <Show when={state()!.question} fallback={<div class="alert alert-warning">Waiting for question details...</div>}>
                                            <QuizQuestionPreview
                                                question={state()!.question}
                                                readonly={state()!.status !== 'active'}
                                                onSelect={handleAnswerSelect}
                                                hideCorrectAnswers={true}
                                                initialAnswers={currentAnswers()}
                                                hideChoices={state()!.status === 'shown'}
                                            />
                                        </Show>
                                    </Show>
                                    <Show when={state()!.hideQuestionsFromStudents && state()!.question}>
                                        {/* Show only the answer options without the question text. */}
                                        <QuizQuestionPreview
                                            question={{ ...state()!.question, content: '' }}
                                            readonly={state()!.status !== 'active'}
                                            onSelect={handleAnswerSelect}
                                            hideCorrectAnswers={true}
                                            initialAnswers={currentAnswers()}
                                            hideChoices={state()!.status === 'shown'}
                                        />
                                    </Show>
                                </Show>
                            </Show>
                        </Show>
                    </Show>
                </Show>
            </div>
        </>
    );
}
